import { BoundSelectField, Css, useComputed } from "@homebound/beam";
import { BoundAiSelectField, BoundAiTextAreaField, BoundAiTextField } from "src/components/formFields/ai";
import { resolveAiProposalStatus } from "src/components/formFields/ai/utils/bindAiProposal";
import {
  useAiFieldProposalState,
  useAiNamedOptionProposal,
} from "src/components/formFields/ai/utils/useAiFieldProposalState";
import { MaterialType } from "src/generated/graphqlTypes";
import { useReaction } from "src/hooks";
import { useMaterialListingContext } from "src/routes/libraries/material-catalog/material-listing-page/components/material-listing-context/MaterialListingContext";
import {
  MaterialCatalogMetadataDocument,
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialBrandFragment,
  useSaveMaterialBrandMutation,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";

export type MaterialDetailsSectionProps = {
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
};

/**
 * Primary listing-level fields.
 */
export function MaterialDetailsSection(props: MaterialDetailsSectionProps) {
  const { items, brands } = props;
  const { formState, listing } = useMaterialListingContext();
  const [saveMaterialBrand] = useSaveMaterialBrandMutation();

  const type = useComputed(() => formState.type.value, [formState]);

  const nameAfpState = useAiFieldProposalState(formState.fieldProposals, "name");
  const designNoteAfpState = useAiFieldProposalState(formState.fieldProposals, "designNote");
  const descriptionAfpState = useAiFieldProposalState(formState.fieldProposals, "description");
  // The agent stores the brand's name; the form field is the brand's id.
  const brand = useAiNamedOptionProposal(formState.fieldProposals, "brand", brands);

  // Defaults the name to the item's name for Placeholder materials
  useReaction(
    () => [formState.type.value, formState.itemId.value],
    ([type, itemId]) => {
      if (type === MaterialType.Placeholder) {
        const name = items.find((i) => i.id === itemId)?.name;
        formState.name.set(name);
      }
    },
    [formState],
  );

  async function saveBrand(newBrand: string) {
    const { data } = await saveMaterialBrand({
      variables: { input: { name: newBrand } },
      refetchQueries: [MaterialCatalogMetadataDocument],
    });
    if (data) {
      const id = data.saveMaterialBrand.materialBrand.id;
      formState.brandId.set(id);
      // onAddNew does not go through BoundAiSelectField's onSelect, so resolve here.
      // TODO: We may want to add this to beam
      resolveAiProposalStatus(brand.state, brand.parser, id);
    }
  }

  return (
    <div css={Css.df.fdc.gap2.$}>
      <BoundAiTextField label="Name" field={formState.name} aiProposal={nameAfpState} fullWidth />
      <BoundSelectField
        label="Item"
        field={formState.itemId}
        options={items}
        disabled={
          !listing?.canEditItem.allowed
            ? listing?.canEditItem.disabledReasons.map((r) => r.message).join(" ")
            : undefined
        }
        fullWidth
      />
      {/* Placeholders stand in for a product that isn't chosen yet, so they have no brand. */}
      {type !== MaterialType.Placeholder && (
        <BoundAiSelectField
          label="Brand"
          field={formState.brandId}
          options={brands}
          aiProposal={brand.state}
          parser={brand.parser}
          onAddNew={saveBrand}
          fullWidth
        />
      )}
      <BoundAiTextAreaField
        label="Marketing Description"
        tooltip="The client-facing description that appears in Checkout."
        field={formState.description}
        aiProposal={descriptionAfpState}
        preventNewLines={false}
        fullWidth
      />
      <BoundAiTextAreaField
        label="Design Notes"
        tooltip="These notes are for internal use and appear on the finish schedule."
        field={formState.designNote}
        aiProposal={designNoteAfpState}
        preventNewLines={false}
        fullWidth
      />
    </div>
  );
}
